<template>
    <div class="panel panel-default">
        <loading-spinner v-if="loading"></loading-spinner>

        <div v-else class="panel-body">
            <div v-if="subscriptionPeriods.data.length">
                <table class="table table-condensed table-striped">
                    <thead>
                        <tr>
                            <th class="col-xs-2 text-center">
                                <input type="checkbox" v-model="selectAll">
                            </th>

                            <th class="col-xs-7">
                                Period
                            </th>

                            <th class="col-xs-3">
                                Price
                            </th>

                            <th class="col-xs-3">
                                Status
                            </th>

                            <th class="col-xs-5">
                                User
                            </th>

                            <th class="col-xs-4"></th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="subscriptionPeriod in subscriptionPeriods.data">
                            <td class="text-center">
                                <input type="checkbox" v-model="selected" :value="subscriptionPeriod">
                            </td>

                            <td>
                                {{ subscriptionPeriod.starts_at | date }} - {{ subscriptionPeriod.ends_at | date }}
                            </td>

                            <td>
                                {{ subscriptionPeriod.price | price }}
                            </td>

                            <td>
                                <status-label :status="subscriptionPeriod.status"></status-label>
                            </td>

                            <td>
                                <router-link v-if="subscriptionPeriod.user" :to="'/users/' + subscriptionPeriod.user.id + '/edit'">
                                    {{ subscriptionPeriod.user.full_name }}
                                </router-link>

                                <span v-else>
                                    (none)
                                </span>
                            </td>

                            <td class="text-right">
                                <div class="btn-group list-actions">
                                    <button
                                            type="button"
                                            class="btn btn-sm"
                                            :disabled="!canUpdate(subscriptionPeriod)"
                                            @click="edit(subscriptionPeriod)"
                                    >
                                        <i class="fa fa-search"></i>
                                    </button>

                                    <button
                                            type="button"
                                            class="btn btn-sm"
                                            :disabled="!canDestroy(subscriptionPeriod)"
                                            @click="destroy(subscriptionPeriod)"
                                    >
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="dropdown" v-if="selected.length">
                    <button
                            class="btn btn-black btn-sm dropdown-toggle"
                            type="button"
                            data-toggle="dropdown"
                    >
                        <i class="fa fa-cogs"></i> List Actions <span class="caret"></span>
                    </button>

                    <ul class="dropdown-menu">
                        <li v-if="canClose">
                            <a href="#close-subscription-period-modal" data-toggle="modal">
                                <i class="fa fa-times"></i> Close
                            </a>
                        </li>

                        <li v-if="canClose" role="separator" class="divider"></li>

                        <li>
                            <a href="#" @click.prevent="destroySelected">
                                <i class="fa fa-trash"></i> Delete
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div v-else class="text-center" style="margin: 40px">
                <i>
                    The subscription is currently on trial.
                </i>

                <br><br>

                <a href="#create-subscription-period-modal" class="btn btn-black btn-sm" data-toggle="modal">
                    <i class="fa fa-plus"></i> Create Subscription Period
                </a>
            </div>
        </div>

        <close-subscription-period-modal
                :subscriptionPeriod="selected.length ? selected[0] : null"
                @closed="updated"
        ></close-subscription-period-modal>

        <create-subscription-period-modal
                :subscription="subscription"
                @created="subscriptionPeriods.data.push($event)"
        ></create-subscription-period-modal>

        <update-subscription-period-modal
                :subscriptionPeriod="editing"
                @updated="updated"
        ></update-subscription-period-modal>
    </div>
</template>

<script>
    import http from '@/http'
    import modal from '@/modal'
    import notify from '@/notify'
    import CloseSubscriptionPeriodModal from '@/pages/Shared/Modals/CloseSubscriptionPeriod'
    import CreateSubscriptionPeriodModal from '@/pages/Shared/Modals/CreateSubscriptionPeriod'
    import Load from '@/mixins/Load'
    import LoadingSpinner from '@/components/Layout/LoadingSpinner'
    import Permissions from '@/mixins/Permissions'
    import StatusLabel from '@/components/StatusLabel'
    import UpdateSubscriptionPeriodModal from '@/pages/Shared/Modals/UpdateSubscriptionPeriod'

    export default {
        components: {
            CloseSubscriptionPeriodModal,
            CreateSubscriptionPeriodModal,
            LoadingSpinner,
            StatusLabel,
            UpdateSubscriptionPeriodModal
        },

        props: {subscription: {}},

        mixins: [Load, Permissions],

        data() {
            return {
                subscriptionPeriods: null,
                selected: [],
                selectAll: false,
                editing: null
            }
        },

        mounted() {
            this.fetch()
        },

        watch: {
            selectAll(value) {
                this.selected = value ? this.subscriptionPeriods.data : []
            }
        },

        computed: {
            canClose() {
                return this.hasPermission('admin') && this.selected.length === 1 && this.selected[0].status !== 'uninvoiced'
            }
        },

        methods: {
            fetch() {
                this.load({subscriptionPeriods: '/api/v1/subscription-periods?subscription=' + this.subscription.data.id})
            },

            canUpdate(subscriptionPeriod) {
                return subscriptionPeriod.status === 'uninvoiced' || this.hasPermission('admin')
            },

            canDestroy(subscriptionPeriod) {
                return subscriptionPeriod.status === 'uninvoiced'
            },

            destroySelected() {
                modal.confirm('Are you sure that you want to delete the subscription periods?', () => {
                    Promise.all(this.selected.map(subscriptionPeriod => this.getDestroyPromise(subscriptionPeriod)))
                        .then(() => {
                            notify.success('The subscription periods were deleted')

                            this.resetSelections()

                            this.fetch()
                        })
                        .catch(error => {
                            if(error.response.status === 400) {
                                notify.warning('Not all subscription periods were deleted')

                                this.resetSelections()

                                this.fetch()
                            }
                        })
                })
            },

            destroy(subscriptionPeriod) {
                modal.confirm('Are you sure that you want to delete the subscription period?', () => {
                    this.getDestroyPromise(subscriptionPeriod)
                        .then(() => {
                            notify.success('The subscription period was deleted')

                            this.fetch()
                        })
                })
            },

            getDestroyPromise(subscriptionPeriod) {
                return http.delete('/api/v1/subscription-periods/' + subscriptionPeriod.id)
            },

            resetSelections() {
                this.selected = []

                this.selectAll = false
            },

            edit(subscriptionPeriod) {
                this.editing = subscriptionPeriod

                $('#update-subscription-period-modal').modal('show')
            },

            updated(subscriptionPeriod) {
                this.resetSelections()

                this.subscriptionPeriods.data.splice(
                    this.subscriptionPeriods.data.findIndex(existing => existing.id === subscriptionPeriod.id),
                    1,
                    subscriptionPeriod
                )
            }
        }
    }
</script>
